import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { usePortal } from '@/contexts/PortalContext'
import { TemplateSection } from '@/lib/portalApi'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
}

export function CertificationSummaryModal({ isOpen, onClose, onConfirm }: Props) {
  const { data, answers } = usePortal()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const sections: TemplateSection[] = (data?.template.sections ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  function displayValue(fieldId: string): string {
    const val = answers[fieldId]
    if (val === null || val === undefined || String(val).trim() === '') return '—'
    return String(val)
  }

  async function handleConfirm() {
    setIsSubmitting(true)
    try {
      await onConfirm()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erreur lors de la soumission'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Récapitulatif de vos données</DialogTitle>
          <DialogDescription>
            {`Ces données seront transmises à ${data?.organization.name ?? '…'}. Votre document source n'est pas partagé.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {sections.map((section) => (
            <div key={section.id}>
              <h3 className="font-semibold text-sm text-foreground mb-3 border-b pb-1">
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.fields.map((field) => (
                  <div key={field.id} className="flex items-start justify-between gap-4 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-muted-foreground truncate">{field.label}</span>
                      {field.required ? (
                        <Badge variant="destructive" className="text-xs flex-shrink-0">Requis</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs flex-shrink-0">Optionnel</Badge>
                      )}
                    </div>
                    <span className="text-right font-medium text-foreground break-words max-w-[50%]">
                      {displayValue(field.id)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded border disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Retour au formulaire
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 text-sm rounded text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--supplier-primary)' }}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Certifier et envoyer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
